import { z } from "zod";
import type { SiteGraph } from "../site/site-graph.js";
import { siteGraph } from "../site/site-graph.js";
import {
  decodeBreakerInstallation,
  encodeBreakerInstallation,
} from "./circuit.js";
import { decodeDevice, encodeDevice } from "./device.js";
import { CodecError } from "./envelope.js";
import {
  decodePanel as decodePanelValue,
  encodePanel as encodePanelValue,
  panelJsonSchema,
} from "./panel.js";
import { decodeSite, encodeSite, siteJsonSchema } from "./site.js";
import { deviceJsonSchema } from "./device.js";

/**
 * Phase 6a codec for `SiteGraph` — the cross-aggregate root that ties
 * a `Site` together with its panels and devices. Persisted shape mirrors
 * the in-memory aggregate exactly: a single `site`, ordered `panels`
 * (each carrying installations + circuits), and ordered `devices`.
 *
 * Rooms are *not* embedded in the SiteGraph payload — they live on the
 * site/room repository in Phase 6b and travel through their own payload
 * variant. The `device` smart factory's room/circuit cross-checks
 * therefore can't run at decode time; the codec re-builds the device
 * value directly (via `decodeDevice`) and trusts the upstream
 * repository boundary to enforce cross-aggregate consistency on save.
 *
 * The `siteGraph()` smart factory IS re-run on decode: it enforces
 * panel-claimed-by-exactly-one-building and device-site-mismatch, which
 * the codec can prove from the persisted shape alone.
 */
export const siteGraphJsonSchema = z.object({
  site: siteJsonSchema,
  panels: z.array(panelJsonSchema),
  devices: z.array(deviceJsonSchema),
});

export type SiteGraphJson = z.infer<typeof siteGraphJsonSchema>;

/**
 * Encode a `SiteGraph` to its canonical JSON shape. The `panels` array
 * preserves graph order; each panel encodes through `encodePanelValue`
 * with the installation-encoder injected so the cross-module cycle
 * stays explicit.
 */
export function encodeSiteGraph(graph: SiteGraph): Record<string, unknown> {
  return {
    site: encodeSite(graph.site),
    panels: graph.panels.map((p) =>
      encodePanelValue(p, encodeBreakerInstallation),
    ),
    devices: graph.devices.map(encodeDevice),
  };
}

/**
 * Decode a `SiteGraph` from its canonical JSON shape. Validates the
 * top-level frame, reconstructs each aggregate, and runs the `siteGraph`
 * smart factory so the cross-aggregate invariants are re-checked on
 * read.
 */
export function decodeSiteGraph(input: unknown): SiteGraph {
  const parsed = siteGraphJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeSiteGraph: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  const site = decodeSite(j.site);
  const panels = j.panels.map((p) =>
    decodePanelValue(p, decodeBreakerInstallation),
  );
  const devices = j.devices.map(decodeDevice);
  return siteGraph({ site, panels, devices });
}
